import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  type LucideIcon,
  Megaphone,
  X,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/utils';

import { IconButton } from './IconButton';

/**
 * Banner — pas komunikatu na PEŁNĄ SZEROKOŚĆ u góry strony (Figma „08.7 · Feedback / Banner").
 *
 * Tony semantyczne (info/success/warning/error) + `brand` (baner marketingowy/systemowy).
 * Tło `*-subtle`, dolna krawędź `*-border`, tekst `*-text`, ikona 20px w kolorze base. Slot
 * `actions` (prawa strona) i opcjonalny „X". W odróżnieniu od `Alert` (wyspa inline) baner nie
 * ma zaokrągleń ani bocznych ramek — przylega do krawędzi obszaru. Tokeny STAŁE (index.css).
 */

export type BannerTone = 'info' | 'success' | 'warning' | 'error' | 'brand';

const bannerVariants = cva('flex w-full items-center gap-3 border-b px-4 py-2.5 text-sm', {
  variants: {
    tone: {
      info: 'bg-[rgb(var(--color-info-subtle))] border-[rgb(var(--color-info-border))] text-[rgb(var(--color-info-text))]',
      success:
        'bg-[rgb(var(--color-success-subtle))] border-[rgb(var(--color-success-border))] text-[rgb(var(--color-success-text))]',
      warning:
        'bg-[rgb(var(--color-warning-subtle))] border-[rgb(var(--color-warning-border))] text-[rgb(var(--color-warning-text))]',
      error:
        'bg-[rgb(var(--color-error-subtle))] border-[rgb(var(--color-error-border))] text-[rgb(var(--color-error-text))]',
      brand:
        'bg-[rgb(var(--color-brand-subtle))] border-[rgb(var(--color-brand-border))] text-[rgb(var(--color-brand-text))]',
    },
  },
  defaultVariants: { tone: 'info' },
});

const TONE_ICON: Record<BannerTone, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
  brand: Megaphone,
};

const TONE_ICON_COLOR: Record<BannerTone, string> = {
  info: 'text-[rgb(var(--color-info))]',
  success: 'text-[rgb(var(--color-success))]',
  warning: 'text-[rgb(var(--color-warning))]',
  error: 'text-[rgb(var(--color-error))]',
  brand: 'text-[rgb(var(--color-brand))]',
};

export interface BannerProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof bannerVariants> {
  tone?: BannerTone;
  /** Treść banera. */
  children?: ReactNode;
  /** Nadpisanie ikony (domyślnie wg tonu). */
  icon?: LucideIcon;
  /** Slot akcji po prawej (np. przycisk). */
  actions?: ReactNode;
  /** Gdy podane — renderuje przycisk zamknięcia. */
  onDismiss?: () => void;
  /** Etykieta a11y przycisku zamknięcia. */
  dismissLabel?: string;
}

export function Banner({
  tone = 'info',
  children,
  icon,
  actions,
  onDismiss,
  dismissLabel = 'Zamknij',
  className,
  ...rest
}: BannerProps) {
  const IconComponent = icon ?? TONE_ICON[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(bannerVariants({ tone }), className)}
      {...rest}
    >
      <IconComponent
        size={20}
        aria-hidden="true"
        className={cn('shrink-0', TONE_ICON_COLOR[tone])}
      />
      <span className="min-w-0 flex-1">{children}</span>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      {onDismiss && (
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={dismissLabel}
          onClick={onDismiss}
          className="-mr-1 shrink-0"
        >
          <X />
        </IconButton>
      )}
    </div>
  );
}
